import APIClient from './index';
import _ from 'lodash';

export const updateConciergeThread = (threadId, updates) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`/ConciergeThreads/${threadId}`, updates)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeThreadsForAdmin = () => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/ConciergeThreads/all_for_admin`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeThreads = ({ page, limit, ConciergePromptConfig_id } = {}) => {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    if (page != null) params.set('page', page);
    if (limit != null) params.set('limit', limit);
    if (ConciergePromptConfig_id) params.set('ConciergePromptConfig_id', ConciergePromptConfig_id);
    APIClient.get(`/ConciergeThreads?${params.toString()}`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeThread = id => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/ConciergeThreads/${id}`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeLeaderboard = params => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/Concierge/leaderboard?${new URLSearchParams(params || {}).toString()}`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeEditors = () => {
  return new Promise((resolve, reject) => {
    APIClient.get('/Concierge/editors')
      .then(response => resolve(response.data.editors || []))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeBrands = ({ search, ids } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (ids && ids.length) params.ids = ids.join(',');
  return new Promise((resolve, reject) => {
    APIClient.get('/Concierge/brands', { params })
      .then(response => resolve(response.data.brands || []))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeShoppers = ({ search, ids } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (ids && ids.length) params.ids = ids.join(',');
  return new Promise((resolve, reject) => {
    APIClient.get('/Concierge/shoppers', { params })
      .then(response => resolve(response.data.shoppers || []))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeShopperDetail = userId => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/Concierge/shopper/${userId}/detail`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeEditorClaims = editorId => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/Concierge/editor/${editorId}/claims`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const deleteConciergeClaim = claimId => {
  return new Promise((resolve, reject) => {
    APIClient.delete(`/ConciergeRequestClaims/${claimId}/delete_with_request`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeRequestCodes = () => {
  return new Promise((resolve, reject) => {
    APIClient.get('/ConciergeRequestCodes')
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

// Creates a code. Pass User_ids to gate it to an allow-list (isGated); omit for an open code.
export const createConciergeRequestCode = data => {
  return new Promise((resolve, reject) => {
    APIClient.post('/ConciergeRequestCodes/create_code', data)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

// Updates a code's definition and reconciles its allow-list from the full User_ids list.
export const updateConciergeRequestCode = updates => {
  return new Promise((resolve, reject) => {
    APIClient.put(`/ConciergeRequestCodes/by_code`, updates)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

// Deletes a code and all of its per-user redemption rows.
export const deleteConciergeRequestCode = code => {
  return new Promise((resolve, reject) => {
    APIClient.delete(`/ConciergeRequestCodes/by_code/${encodeURIComponent(code)}`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

// The full allow-list (granted User_ids) for a gated code — used to prefill the edit form.
export const getConciergeRequestCodeAllowedUserIds = code => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/ConciergeRequestCodes/by_code/${encodeURIComponent(code)}/allowed_user_ids`)
      .then(response => resolve(response.data.userIds || []))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

// Every user who redeemed a code and how many times each. The modal filters/sorts in memory.
export const getConciergeRequestCodeRedemptions = code => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/ConciergeRequestCodes/by_code/${encodeURIComponent(code)}/redemptions`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getConciergeCuratorApplications = (params = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/ConciergeCuratorApplications?${new URLSearchParams(params).toString()}`)
      .then(response => resolve({ applications: response.data.applications, totalCount: response.data.totalCount }))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const updateConciergeCuratorApplication = (id, updates) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`/ConciergeCuratorApplications/${id}`, updates)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const testWelcomeMessage = (mockUserType = 'just_signed_up') => {
  return new Promise((resolve, reject) => {
    APIClient.post('/Concierge/test_welcome', { mockUserType })
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const previewBroadcastCohort = filters => {
  return new Promise((resolve, reject) => {
    APIClient.post('/Concierge/preview_broadcast_cohort', filters || {})
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getCohortBrandFacets = filters => {
  return new Promise((resolve, reject) => {
    APIClient.post('/Concierge/cohort_brand_facets', filters || {})
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const listBroadcasts = () => {
  return new Promise((resolve, reject) => {
    APIClient.get('/Concierge/broadcasts')
      .then(response => resolve(response.data.broadcasts || []))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const recordBroadcast = payload => {
  return new Promise((resolve, reject) => {
    APIClient.post('/Concierge/broadcasts', payload)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const sendBroadcastTestText = ({ phone, body }) => {
  return new Promise((resolve, reject) => {
    APIClient.post('/Concierge/broadcasts/test_text', { phone, body })
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getBroadcastMetrics = id => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/Concierge/broadcasts/${id}/metrics`)
      .then(response => resolve(response.data.metrics))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const getBroadcastRecipients = id => {
  return new Promise((resolve, reject) => {
    APIClient.get(`/Concierge/broadcasts/${id}/recipients`)
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const retryBroadcastFailures = id => {
  return new Promise((resolve, reject) => {
    APIClient.post(`/Concierge/broadcasts/${id}/retry_failed`, {})
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const updateBroadcast = (id, updates) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`/Concierge/broadcasts/${id}`, updates)
      .then(response => resolve(response.data.broadcast))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};

export const cancelBroadcast = id => {
  return new Promise((resolve, reject) => {
    APIClient.post(`/Concierge/broadcasts/${id}/cancel`, {})
      .then(response => resolve(response.data))
      .catch(error => reject(_.get(error, ['data', 'error'])));
  });
};
